import { Modal } from '@/components/_common';
import ModalFooterButtons from '@/components/_common/Modal/ModalFooterButtons';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useToast } from '@/components/_common/Toast';
import FilesList from '@/components/Packaging/InventoryAssignmentModal/FilesList';
import FiltersSidebar from '@/components/Packaging/InventoryAssignmentModal/FiltersSidebar';
import PreviewPanel from '@/components/Packaging/InventoryAssignmentModal/PreviewPanel';
import { isOverlayFit, parseFrameCount } from '@/components/Packaging/utils';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectFileTypeGroupings, selectFileTypes } from '@/redux/selectors/constantsSelectors';
import { selectSelectedVersionId, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import {
  selectInventoryModalPreviewedFile,
  selectInventoryModalSelectedFile,
  selectPackagingActiveCutType
} from '@/redux/selectors/packagingSelectors';
import { selectOverlayLayerByOverlayId, selectTimelineFramesCount } from '@/redux/selectors/packagingTimelineSelectors';
import { selectActiveTitleId, selectActiveVersions } from '@/redux/selectors/titleSelectors';
import {
  selectActiveTitle,
  selectActiveTitleId as selectActiveTitleIdV2,
  selectActiveVersions as selectActiveVersionsV2,
  selectIsActiveTitleLoading
} from '@/redux/selectors/titleV2Selectors';
import { acceptVersionsFromTitleSlice, resetFilters, setFilters } from '@/redux/slices/orderPlacementSlice';
import { setActiveCutType, setInventoryModalPreviewedFile } from '@/redux/slices/packagingSlice';
import { DRAGGABLE_TYPES, Source } from '@/redux/slices/packagingSlice.types';
import { setOverlaySource, setSegmentSource } from '@/redux/slices/packagingTimelineSlice';
import { clearTitle, getTitleDetails, setActiveTitle as setActiveTitleId } from '@/redux/slices/titleSlice';
import {
  clearTitleSlice as clearTitleV2,
  fetchTitleV2 as getTitleDetailsV2,
  setActiveTitleId as setActiveTitleIdV2
} from '@/redux/slices/titleV2Slice';
import styled from '@emotion/styled';
import { IconButton, Tooltip } from '@mui/material';
import React, { useContext, useEffect, useRef } from 'react';
import { DRAGGABLE_TYPES_ALLOWED_MEDIA, IMAGE_LABELS, MASTER_VIDEO_LABELS } from '../constants';

interface Props {
  initialTitleId?: number;
  initialVersionId?: number;
}

const InventoryAssignmentModal: React.FC<Props> = ({ initialTitleId, initialVersionId }) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const activeCutType = useAppSelector(selectPackagingActiveCutType);
  const fileTypes = useAppSelector(selectFileTypes);
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);
  const selectedFile = useAppSelector(selectInventoryModalSelectedFile);
  const previewedFile = useAppSelector(selectInventoryModalPreviewedFile);
  const activeTitleId = useAppSelector(isTitleV2 ? selectActiveTitleIdV2 : selectActiveTitleId);
  const activeTitle = useAppSelector(selectActiveTitle);
  const activeVersions = useAppSelector(isTitleV2 ? selectActiveVersionsV2 : selectActiveVersions);
  const isActiveTitleLoading = useAppSelector(selectIsActiveTitleLoading);
  const selectedVersionId = useAppSelector(selectSelectedVersionId);
  const versionKeys = useAppSelector(selectVersionKeys);
  const activeOverlayLayer = useAppSelector((state) =>
    selectOverlayLayerByOverlayId(state, activeCutType && activeCutType.type === DRAGGABLE_TYPES.OVERLAY ? activeCutType.draggableId : null)
  );
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);

  /**
   * We store `preselectedTitleIdRef` and `preselectedVersionIdRef` in refs instead of the state because changes in these
   * values should not cause re-rendering. They are needed only the moment the modal is opened.
   */
  const preselectedTitleIdRef = useRef<number | null>(initialTitleId || null);
  const preselectedVersionIdRef = useRef<number | undefined>(initialVersionId);

  const { error } = useToast();

  const previewedFileType = previewedFile ? fileTypes[previewedFile.file_type] : undefined;
  const isPreviewSupported = previewedFileType && ['video', 'image'].includes(previewedFileType);

  const isModalOpen = Boolean(activeCutType);

  useEffect(() => {
    if (!isModalOpen || preselectedTitleIdRef.current === null) {
      return;
    }

    if (isTitleV2) {
      dispatch(getTitleDetailsV2(preselectedTitleIdRef.current));
      dispatch(setActiveTitleIdV2(preselectedTitleIdRef.current));
    } else {
      dispatch(getTitleDetails(preselectedTitleIdRef.current));
      dispatch(setActiveTitleId(preselectedTitleIdRef.current));
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (activeTitleId !== null) {
      preselectedTitleIdRef.current = activeTitleId;
    }
  }, [activeTitleId]);

  useEffect(() => {
    if (selectedVersionId !== null) {
      preselectedVersionIdRef.current = selectedVersionId;
    }
  }, [selectedVersionId]);

  useEffect(() => {
    if (activeTitleId !== null && activeVersions && activeVersions.length > 0) {
      dispatch(acceptVersionsFromTitleSlice({ versions: activeVersions, id: activeTitleId }));
    }
  }, [activeTitleId, activeVersions]);

  // Fetch title details when `activeTitleId` changes if `activeTitle` is not yet loaded (or loading at the moment).
  // `activeTitleId` can change when a user clicks the "View Inventory" button inside the Title Browser (for example, when browsing series).
  // We need it only for V2 titles API, because V1 API loads the whole title hierarchy with single request,
  // when series loaded every child is already in the store, so no need to load titles separately.
  useEffect(() => {
    if (!isTitleV2) {
      return;
    }

    if (!activeTitle && !isActiveTitleLoading && activeTitleId) {
      dispatch(getTitleDetailsV2(activeTitleId));
    }
  }, [activeTitleId, activeTitle, isActiveTitleLoading]);

  const cleanUp = () => {
    dispatch(setActiveCutType(null));
    dispatch(resetFilters(undefined));
    dispatch(setInventoryModalPreviewedFile(null));
  };

  const unmountCleanUp = () => {
    cleanUp();
    if (isTitleV2) {
      dispatch(clearTitleV2());
    } else {
      dispatch(clearTitle());
    }
  };

  const closeModalCleanUp = () => {
    cleanUp();

    // titleV2Slice's `clearTitleSlice` and titleSlice's `clearTitle` work differently.
    // `clearTitleSlice` clears all loaded entities from the store, so we don't want to call it when modal is closed
    if (!isTitleV2) {
      dispatch(clearTitle());
    }
  };

  useEffect(() => {
    if (activeCutType) {
      const { video, audio, subtitle, image } = fileTypeGroupings;
      const allowedMediaTypes = DRAGGABLE_TYPES_ALLOWED_MEDIA[activeCutType.type] || [];
      dispatch(
        setFilters({
          version_name_filter: [],
          file_type_filter: [
            ...(allowedMediaTypes.includes('video') ? video : []),
            ...(allowedMediaTypes.includes('audio') ? audio : []),
            ...(allowedMediaTypes.includes('subtitle') ? subtitle : []),
            ...(allowedMediaTypes.includes('image') ? image : [])
          ],
          cut_type_filter: [activeCutType.kind],
          prefix_search: [
            ...MASTER_VIDEO_LABELS,
            ...(activeCutType.type === DRAGGABLE_TYPES.OVERLAY ? IMAGE_LABELS : []) // Also request images when `activeCutType` is overlay]
          ]
        })
      );
    }
  }, [activeCutType]);

  useEffect(() => () => unmountCleanUp(), []);

  const handleInsertFile = async () => {
    if (!selectedFile || !activeCutType || activeTitleId === null) {
      return;
    }

    const mediaType = fileTypes[selectedFile.file_type];
    const isVideo = mediaType && mediaType === 'video';

    if (isVideo && !selectedFile.proxyId) {
      error('Selected file does not have corresponding proxy file');
      return;
    }

    const frameCountRaw: string | undefined = selectedFile.media_info.MediaInfo?.General?.FrameCount;
    const frameCount = frameCountRaw ? parseFrameCount(frameCountRaw) : null;
    const invalidDurationMessage = 'Selected file has an invalid duration';
    const version = versionKeys[selectedFile.version_id];
    const baseSourceProps: Source = {
      titleId: `${selectedFile.version_id}`,
      titleName: version.name,
      inventoryId: `${selectedFile.id}`,
      is_screened: version.is_screened,
      use_window_start: version.metadata_values?.use_window_start ?? null,
      use_window_end: version.metadata_values?.use_window_end ?? null
    };

    if (activeCutType.type === DRAGGABLE_TYPES.SEGMENT) {
      if (frameCount === null) {
        error(invalidDurationMessage);
        return;
      }

      dispatch(
        setSegmentSource({
          segmentId: activeCutType.draggableId,
          source: {
            ...baseSourceProps,
            proxyInventoryId: `${selectedFile.proxyId}`
          },
          duration: frameCount,
          subtitle: selectedFile.name
        })
      );
    } else if (activeCutType.type === DRAGGABLE_TYPES.OVERLAY) {
      if (fileTypes[selectedFile.file_type] === 'image') {
        dispatch(
          setOverlaySource({
            overlayId: activeCutType.draggableId,
            sourceType: 'image',
            source: {
              ...baseSourceProps
            },
            subtitle: selectedFile.name
          })
        );
      } else if (fileTypes[selectedFile.file_type] === 'video') {
        if (frameCount === null) {
          error(invalidDurationMessage);
          return;
        }

        if (!activeOverlayLayer) {
          // Type guard. This error should never happen since `activeCutType` should be on the timeline when this code is called.
          throw new Error("Can not find selected overlay's layer. Possibly activeCutType is not an overlay");
        }

        const willFit = isOverlayFit(timelineFramesCount, activeOverlayLayer, {
          draggableId: activeCutType.draggableId,
          start: activeCutType.start,
          end: activeCutType.start + frameCount
        });

        if (!willFit.fit) {
          const errorToastOptions = { autoHideDuration: 12000 }; // extended duration to make sure users will have time to read the message
          if (willFit.longerThanTimeline) {
            error('Selected asset is longer than a timeline. Please pick another asset.', errorToastOptions);
          } else if (willFit.outsideTimeline) {
            error('The overlay extends beyond the timeline boundaries. Please reposition it.', errorToastOptions);
          }

          if (willFit.overlayOverlap)
            error('The overlay overlaps another overlay in the layer. Please reposition it or move it to another layer.', errorToastOptions);
          // We're not calling `return` on purpose when overlay doesn't fit. Even thought it will not fit we still want to add it to the timeline.
        }

        dispatch(
          setOverlaySource({
            overlayId: activeCutType.draggableId,
            sourceType: 'video',
            duration: frameCount,
            source: {
              ...baseSourceProps,
              proxyInventoryId: `${selectedFile.proxyId}`
            },
            subtitle: selectedFile.name
          })
        );
      } else {
        error('Currently, only images are supported for overlays.');
        return;
      }
    }

    closeModalCleanUp();
  };

  return (
    <StyledModal open={Boolean(activeCutType)} onClose={closeModalCleanUp} title="Inventory Assignment" noHeader scrollDisabled>
      <InnerWrapper>
        <FiltersSidebar />
        <Content>
          <MainContent>
            <FilesList initialVersionId={preselectedVersionIdRef.current} />
            {activeTitleId !== null && previewedFile && isPreviewSupported && <PreviewPanel titleId={activeTitleId} previewedFile={previewedFile} />}
          </MainContent>
          <StyledFooter>
            <ModalFooterButtons
              submit={{
                text: 'Insert File',
                onClick: handleInsertFile,
                disabled: !selectedFile
              }}
              cancel={closeModalCleanUp}
            />
          </StyledFooter>
        </Content>
        <Tooltip title="Close modal">
          <CloseButtonWrapper>
            <CloseButton onClick={closeModalCleanUp}>
              <PolarisIcon icon="close" />
            </CloseButton>
          </CloseButtonWrapper>
        </Tooltip>
      </InnerWrapper>
    </StyledModal>
  );
};

export default InventoryAssignmentModal;

const StyledModal = styled(Modal)`
  > .MuiPaper-root {
    width: 95vw;
    height: 80vh;
    max-width: none;
    overflow: hidden;
    padding: 0;
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? `
     radial-gradient(
        107.39% 121.2% at 65.13% -30.51%,
        rgba(125, 138, 243, 0.5) 13.92%,
        rgba(69, 83, 196, 0.5) 43%,
        rgba(57, 69, 162, 0.5) 62%,
        rgba(33, 40, 94, 0.5) 100%
      ),
      rgba(27, 29, 39, 1)`
        : theme.palette.background.paper};
  }

  .MuiBox-root {
    padding: 0;
    max-height: none;
    height: 100%;

    .MuiPaper-root {
      background: transparent;
      box-shadow: none;
    }
  }

  .MuiAccordion-root.MuiPaper-root {
    background: transparent;
    min-height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    .MuiAccordionSummary-root {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const Content = styled.div`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.div`
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
`;

const StyledFooter = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper + '7a'};

  > div {
    border-top: none;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(2)};
`;

const CloseButton = styled(IconButton)`
  opacity: 0.5;
  transition: ease opacity 250ms;
  &:hover {
    opacity: 1;
  }
`;
